"use client";

import * as React from "react";
import { cn } from "./utils";

interface MenubarProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

function Menubar({ className, children, ...props }: MenubarProps) {
  return (
    <div
      data-slot="menubar"
      className={cn(
        "flex h-10 items-center space-x-1  border bg-background p-1",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function MenubarMenu({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div data-slot="menubar-menu" {...props}>
      {children}
    </div>
  );
}

function MenubarTrigger({ className, children, ...props }: React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      data-slot="menubar-trigger"
      className={cn(
        "flex cursor-default select-none items-center  px-3 py-1.5 text-sm font-medium outline-none focus:bg-blue-50 focus:text-blue-700 data-[highlighted]:bg-blue-50 data-[highlighted]:text-blue-700 data-[state=open]:bg-blue-50 data-[state=open]:text-blue-700",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function MenubarContent({ className, align = "start", alignOffset = -4, sideOffset = 8, children, ...props }: React.HTMLAttributes<HTMLDivElement> & {
  align?: "start" | "center" | "end";
  alignOffset?: number;
  sideOffset?: number;
}) {
  return (
    <div
      data-slot="menubar-content"
      className={cn(
        "z-50 min-w-[12rem] overflow-hidden  border bg-popover p-1 text-popover-foreground shadow-md",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function MenubarItem({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="menubar-item"
      className={cn(
        "relative flex cursor-default select-none items-center  px-2 py-1.5 text-sm outline-none focus:bg-blue-50 focus:text-blue-700 data-[highlighted]:bg-blue-50 data-[highlighted]:text-blue-700 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function MenubarCheckboxItem({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="menubar-checkbox-item"
      className={cn(
        "relative flex cursor-default select-none items-center  py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-blue-50 focus:text-blue-700 data-[highlighted]:bg-blue-50 data-[highlighted]:text-blue-700 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function MenubarRadioItem({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="menubar-radio-item"
      className={cn(
        "relative flex cursor-default select-none items-center  py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-blue-50 focus:text-blue-700 data-[highlighted]:bg-blue-50 data-[highlighted]:text-blue-700 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function MenubarLabel({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="menubar-label"
      className={cn("px-2 py-1.5 text-sm font-semibold", className)}
      {...props}
    >
      {children}
    </div>
  );
}

function MenubarSeparator({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="menubar-separator"
      className={cn("-mx-1 my-1 h-px bg-muted", className)}
      {...props}
    />
  );
}

function MenubarShortcut({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      data-slot="menubar-shortcut"
      className={cn("ml-auto text-xs tracking-widest text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
  MenubarContent,
  MenubarItem,
  MenubarCheckboxItem,
  MenubarRadioItem,
  MenubarLabel,
  MenubarSeparator,
  MenubarShortcut,
};
